// A DNS label: 1-63 chars, alphanumeric at both ends, hyphens only in between.
// The optional group is what allows a 1-char label while still forbidding a
// trailing hyphen, and {0,61} caps the label at 63 (1 + 61 + 1).
const LABEL_REGEX = '[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?';
// Same shape, but the TLD must begin with a letter. That also rejects every
// IPv4 literal, since a dotted quad's final label is numeric.
const TLD_REGEX = '[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?';
// Two or more labels, 253 chars max overall. Safe against a lowercased,
// punycoded hostname only -- run URL.parse first, never raw input.
const HOSTNAME_REGEX = new RegExp(`^(?=.{1,253}$)(?:${LABEL_REGEX}\\.)+${TLD_REGEX}$`);

const IPV4 = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;

// IANA special-purpose IPv4 ranges: never valid as a public destination.
// Ordered as in the registry; the CIDR sizes are the whole point, so the
// /10, /12 and /15 entries are the ones worth having tests around.
/* eslint-disable @typescript-eslint/no-magic-numbers */
const RESERVED_V4: Array<[string, number]> = [
	['0.0.0.0', 8], // "this network"
	['10.0.0.0', 8], // private
	['100.64.0.0', 10], // carrier-grade NAT
	['127.0.0.0', 8], // loopback
	['169.254.0.0', 16], // link-local, incl. cloud metadata
	['172.16.0.0', 12], // private
	['192.0.0.0', 24], // IETF protocol assignments
	['192.0.2.0', 24], // TEST-NET-1
	['192.88.99.0', 24], // 6to4 relay anycast (deprecated)
	['192.168.0.0', 16], // private
	['198.18.0.0', 15], // benchmarking
	['198.51.100.0', 24], // TEST-NET-2
	['203.0.113.0', 24], // TEST-NET-3
	['224.0.0.0', 4], // multicast
	['240.0.0.0', 4] // reserved, incl. 255.255.255.255 broadcast
];
/* eslint-enable @typescript-eslint/no-magic-numbers */

export function normalizeUrl(value: string): string {
	return URL.parse(value)?.href ?? value;
}

/**
 * NOTE: there is another `isUrl` validation missaligned with this one in libs/features/sendbird-chat/src/components/channel-conversation/components/message-content/utils.ts
 * Considering it's very different and will produce a lot of false-positves, I didn't align it here; still worth being recorded
 * Known gaps:
 * nonexistent TLD: 'http://example.invalid'
 * reserved TLD: 'http://foo.test'
 */
export function isValidPublicUrl(value: string) {
	const url = URL.parse(value);

	if (!url) {
		return false;
	}

	if (url.protocol !== 'https:' && url.protocol !== 'http:') {
		return false;
	}

	// credential-stuffing vector
	if (url.username || url.password) {
		return false;
	}

	if (IPV4.test(url.hostname)) {
		return isPublicIPv4(url.hostname);
	}

	return HOSTNAME_REGEX.test(url.hostname);
}

const IPV4_OCTET_MAX = 255;
const IPV4_OCTET_RADIX = 256;
const IPV4_BIT_WIDTH = 32;
const IPV4_ALL_ONES = 0xffffffff;

const RESERVED_V4_MASKED = RESERVED_V4.map(([base, bits]) => {
	const mask = (IPV4_ALL_ONES << (IPV4_BIT_WIDTH - bits)) >>> 0;
	return [(toUint32(base) & mask) >>> 0, mask];
});

// Only ever called with a hostname URL.parse produced, so the dotted quad is
// already canonical -- 0x7f.1, 2130706433 and 0177.0.0.1 all arrive as 127.0.0.1
function isPublicIPv4(hostname) {
	const octets = hostname.split('.');

	if (octets.some(o => Number(o) > IPV4_OCTET_MAX)) {
		return false;
	}

	const n = toUint32(hostname);

	return !RESERVED_V4_MASKED.some(([base, mask]) => (n & mask) >>> 0 === base);
}

function toUint32(quad) {
	return quad.split('.').reduce((acc, oct) => acc * IPV4_OCTET_RADIX + Number(oct), 0);
}
